import { useState } from 'react'
import Modal from '../shared/Modal'

export interface ViewSettings {
  showYtd: boolean
  showAverage: boolean
  showTeam: boolean
  showCategory: boolean
  highlightCurrentWeek: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  settings: ViewSettings
  onSave: (settings: ViewSettings) => void
}

const TOGGLE_OPTIONS: { key: keyof ViewSettings; label: string; description: string }[] = [
  { key: 'showYtd', label: 'YTD Total', description: 'Show year-to-date total column' },
  { key: 'showAverage', label: 'Average', description: 'Show average value column' },
  { key: 'showTeam', label: 'Team', description: 'Show team name column' },
  { key: 'showCategory', label: 'Category', description: 'Show category column' },
  { key: 'highlightCurrentWeek', label: 'Current Week Highlight', description: 'Highlight the current week column' },
]

export default function EditViewModal({ isOpen, onClose, settings, onSave }: Props) {
  const [localSettings, setLocalSettings] = useState<ViewSettings>(settings)

  const handleToggle = (key: keyof ViewSettings) => {
    setLocalSettings(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = () => {
    onSave(localSettings)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Customize View">
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">Choose which columns and features to display.</p>

        <div className="divide-y divide-stone/10">
          {TOGGLE_OPTIONS.map(opt => (
            <div key={opt.key} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-text-primary">{opt.label}</p>
                <p className="text-sm text-text-secondary">{opt.description}</p>
              </div>
              <button
                onClick={() => handleToggle(opt.key)}
                className={`relative w-10 h-6 rounded-full transition-colors ${
                  localSettings[opt.key] ? 'bg-pinnacle-red' : 'bg-stone/30'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    localSettings[opt.key] ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red/90 rounded-lg transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  )
}
